import { readKakaoJsKey } from './kakaoKey'

/**
 * 카카오맵 JS SDK 로더 — **문서에 한 번만 붙인다.**
 *
 * 여기(gui)에서 쓰는 곳은 등록 화면의 「고른 주소 확인」 한 장뿐이다. 해봄지도는 Leaflet이라
 * 이 로더를 쓰지 않는다.
 *
 * **화면을 열자마자 받지 않는다** — 지도를 실제로 그릴 때 처음 부른다.
 * 키가 없거나 콘솔에 도메인이 등록되지 않았으면 실패하고, 부르는 쪽이 그 사실을 다룬다.
 *
 * FE repo `src/shared/lib/kakaoMapSdk.ts`의 사본이되 **타입은 우리가 쓰는 것만** 옮겼다
 * (FE는 해봄지도에도 카카오를 써서 Polygon·CustomOverlay가 더 있다).
 */

let sdkPromise: Promise<KakaoMaps> | null = null

/** 우리가 쓰는 것만 적는다 — SDK 전체 타입을 옮겨 오지 않는다. */
export interface KakaoMaps {
  LatLng: new (lat: number, lng: number) => KakaoLatLng
  Map: new (
    el: HTMLElement,
    opts: { center: KakaoLatLng; level: number; draggable?: boolean },
  ) => KakaoMap
  Marker: new (opts: { position: KakaoLatLng }) => { setMap: (m: KakaoMap | null) => void }
  load: (cb: () => void) => void
}

export interface KakaoLatLng {
  readonly __brand?: 'kakao.maps.LatLng'
}

export interface KakaoMap {
  setCenter: (c: KakaoLatLng) => void
  relayout: () => void
}

declare global {
  interface Window {
    kakao?: { maps?: KakaoMaps }
  }
}

export function loadKakaoMapSdk(): Promise<KakaoMaps> {
  if (sdkPromise !== null) return sdkPromise

  sdkPromise = new Promise((resolve, reject) => {
    const jsKey = readKakaoJsKey()
    if (jsKey === null) {
      reject(new Error('카카오 JS 키가 없다'))
      return
    }

    const el = document.createElement('script')
    // `autoload=false` — 받아지는 즉시가 아니라 우리가 `load()`를 부를 때 뜬다.
    el.src = `https://dapi.kakao.com/v2/maps/sdk.js?appkey=${jsKey}&autoload=false`
    el.async = true
    el.onload = () => {
      const maps = window.kakao?.maps
      if (maps === undefined) {
        reject(new Error('kakao.maps가 없다'))
        return
      }
      maps.load(() => resolve(maps))
    }
    el.onerror = () => reject(new Error('지도 스크립트를 받지 못했다'))
    document.head.appendChild(el)
  })
  return sdkPromise
}
